import { useState } from "react";

export default function CostComparisonSection() {
  const [estimate, setEstimate] = useState("");
  const [result, setResult] = useState<{ holdback: number; outOfPocket: number } | null>(null);

  const handleCalculate = () => {
    const val = parseFloat(estimate.replace(/[^0-9.]/g, ""));
    if (!isNaN(val) && val > 0) {
      const holdback = Math.round(val * 0.32);
      const outOfPocket = Math.round(val * 0.18);
      setResult({ holdback, outOfPocket });
    }
  };

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <section id="comparison" className="py-24 bg-[#f8f5f0]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#f97316]/15 text-[#f97316] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            <i className="ri-scales-line"></i>
            Roofing Cost Comparison
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-[#1a1a2e] leading-tight mb-4">
            Why the Carrier&apos;s Number Looks Low — <br className="hidden md:block" />
            <span className="text-[#f97316]">And Why It Doesn&apos;t Have to Be</span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
            Homeowners see the insurance estimate, compare it to a contractor&apos;s bid, and panic. They think they have to find the cheapest roofer or pay the difference themselves. Neither is true.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          {/* Scenario 1 */}
          <div className="bg-white rounded-2xl p-8 border-2 border-red-100">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-500 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-5">
              <i className="ri-close-circle-line"></i>
              What Most Homeowners Do
            </div>
            <h3 className="text-[#1a1a2e] font-black text-xl mb-4">Chase the Cheapest Bid</h3>
            <ul className="space-y-3 mb-6">
              {[
                "Accept the ACV check as full payment",
                "Find the lowest-priced contractor",
                "Contractor cuts corners to hit the number",
                "Mismatched materials void the warranty",
                "Roof fails in 5–7 years instead of 25+",
                "Never claim the depreciation holdback",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-line text-red-400 text-base"></i>
                  </div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-red-500 font-black text-2xl">$8,000–$15,000</div>
              <div className="text-red-400 text-xs mt-1">Left on the table + future repair costs</div>
            </div>
          </div>

          {/* Scenario 2 */}
          <div className="bg-white rounded-2xl p-8 border-2 border-yellow-100">
            <div className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-600 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-5">
              <i className="ri-alert-line"></i>
              What Some Homeowners Try
            </div>
            <h3 className="text-[#1a1a2e] font-black text-xl mb-4">Pay the Difference Out of Pocket</h3>
            <ul className="space-y-3 mb-6">
              {[
                "Accept the ACV check",
                "Hire a quality contractor",
                "Pay $5,000–$12,000 out of pocket",
                "Use savings, credit, or other funds",
                "Still don't claim the depreciation holdback",
                "Overpay when the money was already there",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-alert-line text-yellow-500 text-base"></i>
                  </div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-yellow-600 font-black text-2xl">$5,000–$12,000</div>
              <div className="text-yellow-500 text-xs mt-1">Unnecessary out-of-pocket expense</div>
            </div>
          </div>

          {/* Scenario 3 - RidgeCap */}
          <div className="bg-[#1a1a2e] rounded-2xl p-8 border-2 border-[#f97316]">
            <div className="inline-flex items-center gap-2 bg-[#f97316]/20 text-[#f97316] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-5">
              <i className="ri-checkbox-circle-line"></i>
              The RidgeCap GC Way
            </div>
            <h3 className="text-white font-black text-xl mb-4">Maximize Every Dollar</h3>
            <ul className="space-y-3 mb-6">
              {[
                "Review full estimate and identify all recoverable items",
                "File supplements for missed scope items",
                "Install with manufacturer-certified materials",
                "Document everything for depreciation claim",
                "Submit completion docs and recover holdback",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[#f97316] text-base"></i>
                  </div>
                  <span className="text-white/80 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
